import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiGetRequest, ApiPaginationResponse, ApiResponse } from 'types';

import { apiBaseQuery } from 'modules/apiBaseQuery';
import { IUser, IUserRole } from 'modules/auth-session';

import {
  BindUserCompaniesPayload,
  CreateApiKeyResponse,
  CreateUserPayload,
  FetchUsersPayload,
  GetUserPayload,
  RemoveUserPayload,
  SwitchUserCompanyPayload,
  UpdateUserPayload,
  UpdateUserProfilePayload,
} from '../types/users.types';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: apiBaseQuery(`${process.env.REACT_APP_API_URL}/v2/api`),
  endpoints: (builder) => ({
    getAvailableRoles: builder.query<ApiResponse<IUserRole[]>, ApiGetRequest>({
      query: (params) => ({
        url: '/admin/roles',
        method: 'GET',
        params,
      }),
    }),
    removeUser: builder.mutation<ApiResponse<any>, RemoveUserPayload>({
      query: (payload) => ({
        url: `/admin/users/${payload.id}`,
        method: 'DELETE',
      }),
    }),
    fetchUsers: builder.query<ApiPaginationResponse<IUser[]>, FetchUsersPayload>({
      query: (params) => ({
        url: '/admin/users',
        method: 'GET',
        params,
      }),
    }),
    getUser: builder.query<ApiResponse<IUser>, GetUserPayload>({
      query: (payload) => ({
        url: `/admin/users/${payload.id}`,
        method: 'GET',
      }),
    }),
    createUser: builder.mutation<ApiResponse<IUser>, CreateUserPayload>({
      query: (payload) => ({
        url: '/admin/users',
        method: 'POST',
        body: payload,
      }),
    }),
    updatedUser: builder.mutation<ApiResponse<IUser>, UpdateUserPayload>({
      query: (payload) => ({
        url: `/admin/users/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
    }),
    bindUserCompanies: builder.mutation<ApiResponse<IUser>, BindUserCompaniesPayload>({
      query: (payload) => ({
        url: `/admin/users/${payload.user_id}/admin-companies`,
        method: 'POST',
        body: payload,
      }),
    }),
    switchUserCompany: builder.mutation<ApiResponse<void>, SwitchUserCompanyPayload>({
      query: (payload) => ({
        url: `/admin/users/${payload.user_id}/company`,
        method: 'PUT',
        body: payload,
      }),
    }),
    updatedUserProfile: builder.mutation<ApiResponse<IUser>, UpdateUserProfilePayload>({
      query: (payload) => ({
        url: `/admin/users/${payload.user_id}/profile`,
        method: 'PUT',
        body: payload,
      }),
    }),
    createApiKey: builder.mutation<ApiResponse<CreateApiKeyResponse>, void>({
      query: () => ({
        url: '/me/api-token',
        method: 'POST',
      }),
    }),
    removeApiKey: builder.mutation<ApiResponse<void>, void>({
      query: () => ({
        url: '/me/api-token',
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useLazyGetAvailableRolesQuery,
  useRemoveUserMutation,
  useLazyFetchUsersQuery,
  useLazyGetUserQuery,
  useCreateUserMutation,
  useUpdatedUserMutation,
  useBindUserCompaniesMutation,
  useSwitchUserCompanyMutation,
  useUpdatedUserProfileMutation,
  useCreateApiKeyMutation,
  useRemoveApiKeyMutation,
} = usersApi;
